import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import {
  Typography,
  Paper,
  Button,
  CircularProgress,
  IconButton,
  Grid,
  Divider,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
} from '@material-ui/core';
import { ArrowBack } from '@material-ui/icons';
import { MCITestRecord } from '../../../constants/Records';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(3),
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: theme.spacing(2),
    },
    breadcrumb: {
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(1),
    },
    backButton: {
      marginRight: theme.spacing(2),
    },
    form: {
      padding: theme.spacing(3),
    },
    questionContainer: {
      marginBottom: theme.spacing(4),
      padding: theme.spacing(2),
      backgroundColor: theme.palette.grey[50],
      borderRadius: theme.shape.borderRadius,
    },
    questionNumber: {
      fontWeight: 'bold',
      color: theme.palette.primary.main,
      marginBottom: theme.spacing(1),
    },
    questionText: {
      fontWeight: 500,
      marginBottom: theme.spacing(1),
    },
    questionHint: {
      color: theme.palette.text.secondary,
      fontSize: '0.875rem',
      marginBottom: theme.spacing(2),
    },
    radioGroup: {
      marginTop: theme.spacing(1),
    },
    totalSection: {
      marginTop: theme.spacing(3),
      padding: theme.spacing(2),
      backgroundColor: theme.palette.grey[100],
      borderRadius: theme.shape.borderRadius,
    },
    totalLabel: {
      fontWeight: 'bold',
      fontSize: '1.2rem',
    },
    totalScore: {
      fontWeight: 'bold',
      fontSize: '1.5rem',
      color: theme.palette.primary.main,
    },
    judgmentNormal: {
      color: theme.palette.success.main,
      fontWeight: 'bold',
    },
    judgmentMCI: {
      color: theme.palette.warning.main,
      fontWeight: 'bold',
    },
    judgmentDementia: {
      color: theme.palette.error.main,
      fontWeight: 'bold',
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: theme.spacing(2),
      marginTop: theme.spacing(3),
    },
    description: {
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(3),
    },
    dateField: {
      marginBottom: theme.spacing(3),
    },
  }),
);

// 質問の定義（HDSR 9問）
const QUESTIONS = [
  {
    id: 1,
    label: '質問1: 年齢',
    question: '歳はいくつですか？',
    hint: '※2年までの誤差は正解',
    options: [
      { value: 0, label: '不正解（0点）' },
      { value: 1, label: '正解（1点）' },
    ],
    maxScore: 1,
  },
  {
    id: 2,
    label: '質問2: 日時の見当識',
    question: '今日は何年の何月何日ですか？何曜日ですか？',
    hint: '※年・月・日・曜日が正解でそれぞれ1点ずつ',
    options: [
      { value: 0, label: '不正解（0点）' },
      { value: 1, label: '1つ正解（1点）' },
      { value: 2, label: '2つ正解（2点）' },
      { value: 3, label: '3つ正解（3点）' },
      { value: 4, label: '全部正解（4点）' },
    ],
    maxScore: 4,
  },
  {
    id: 3,
    label: '質問3: 場所の見当識',
    question: '私たちが今いるところはどこですか？',
    hint: '※正答がない時は5秒後にヒントを与える（例：家ですか？病院ですか？）',
    options: [
      { value: 0, label: '不正解（0点）' },
      { value: 1, label: 'ヒントあり正解（1点）' },
      { value: 2, label: '自発的に正解（2点）' },
    ],
    maxScore: 2,
  },
  {
    id: 4,
    label: '質問4: 3単語の即時再生',
    question: 'これから言う3つの言葉を言ってみてください',
    hint: '※後の設問でまた聞くので、よく覚えてもらう。言えた言葉ごとに各1点',
    options: [
      { value: 0, label: '不正解（0点）' },
      { value: 1, label: '1つ正解（1点）' },
      { value: 2, label: '2つ正解（2点）' },
      { value: 3, label: '3つ正解（3点）' },
    ],
    maxScore: 3,
  },
  {
    id: 5,
    label: '質問5: 計算',
    question: '100から7を引くといくつですか？さらに7を引くと？',
    hint: '※正解: 93, 86（1問目が正解の時のみ2問目を問う）',
    options: [
      { value: 0, label: '不正解（0点）' },
      { value: 1, label: '1つ正解（1点）' },
      { value: 2, label: '2つ正解（2点）' },
    ],
    maxScore: 2,
  },
  {
    id: 6,
    label: '質問6: 数字の逆唱',
    question: 'これから言う数字を逆から言ってください',
    hint: '※1) 6-8-2 → 2-8-6、2) 3-5-2-9 → 9-2-5-3（1問目正解時のみ2問目を問う）',
    options: [
      { value: 0, label: '不正解（0点）' },
      { value: 1, label: '1つ正解（1点）' },
      { value: 2, label: '2つ正解（2点）' },
    ],
    maxScore: 2,
  },
  {
    id: 7,
    label: '質問7: 3単語の遅延再生',
    question: '先ほど覚えてもらった言葉を言ってください',
    hint: '※自発的に答えられたら各2点、ヒントありで答えられたら各1点',
    options: [
      { value: 0, label: '0点' },
      { value: 1, label: '1点' },
      { value: 2, label: '2点' },
      { value: 3, label: '3点' },
      { value: 4, label: '4点' },
      { value: 5, label: '5点' },
      { value: 6, label: '6点' },
    ],
    maxScore: 6,
  },
  {
    id: 8,
    label: '質問8: 5つの物品記銘',
    question: 'これから5つの品物を見せます。それらを隠しますので、何があったか言ってください',
    hint: '※正解: 鍵、ハサミ、お金、扇子、ライター',
    options: [
      { value: 0, label: '不正解（0点）' },
      { value: 1, label: '1つ正解（1点）' },
      { value: 2, label: '2つ正解（2点）' },
      { value: 3, label: '3つ正解（3点）' },
      { value: 4, label: '4つ正解（4点）' },
      { value: 5, label: '5つ正解（5点）' },
    ],
    maxScore: 5,
  },
  {
    id: 9,
    label: '質問9: 野菜の名前（言語流暢性）',
    question: '知っている野菜の名前をできるだけ多く言ってください',
    hint: '※途中で詰まり、約10秒待っても出ない場合にはそこで打ち切る',
    options: [
      { value: 0, label: '5個以下（0点）' },
      { value: 1, label: '6個（1点）' },
      { value: 2, label: '7個（2点）' },
      { value: 3, label: '8個（3点）' },
      { value: 4, label: '9個（4点）' },
      { value: 5, label: '10個以上（5点）' },
    ],
    maxScore: 5,
  },
];

interface Props {
  institutionId: string;
  institutionName: string;
  userId: string;
  userName: string;
  uniqueId: string;
  currentRecord: MCITestRecord | null;
  isEdit: boolean;
  executing: boolean;
  created: boolean;
  updated: boolean;
  onFetchRecord: (uniqueId: string, timestamp: string) => void;
  onCreate: (uniqueId: string, data: { answers: number[]; timestamp?: number }) => void;
  onUpdate: (uniqueId: string, timestamp: string, data: { answers: number[]; timestamp?: number }) => void;
  onResetFlags: () => void;
}

const MCIRecordForm: React.FC<Props> = ({
  institutionId,
  institutionName,
  userId,
  userName,
  uniqueId,
  currentRecord,
  isEdit,
  executing,
  created,
  updated,
  onCreate,
  onUpdate,
  onResetFlags,
}) => {
  const classes = useStyles();
  const history = useHistory();

  // 9つの質問の回答を管理 (-1は未選択)
  const [answers, setAnswers] = useState<number[]>([-1, -1, -1, -1, -1, -1, -1, -1, -1]);

  // テスト実施日（YYYY-MM-DD形式）
  const [testDate, setTestDate] = useState<string>(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });

  useEffect(() => {
    if (currentRecord && isEdit) {
      if (currentRecord.answers && Array.isArray(currentRecord.answers)) {
        setAnswers(currentRecord.answers);
      }
      // timestampから日付を復元
      if (currentRecord.timestamp) {
        const date = new Date(currentRecord.timestamp);
        setTestDate(date.toISOString().split('T')[0]);
      }
    }
  }, [currentRecord, isEdit]);

  useEffect(() => {
    if (created || updated) {
      onResetFlags();
      history.push(`/institutions/${institutionId}/users/${userId}/records/mci`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [created, updated]);

  const handleBack = () => {
    history.push(`/institutions/${institutionId}/users/${userId}/records/mci`);
  };

  const handleAnswerChange = (index: number, value: number) => {
    const newAnswers = [...answers];
    newAnswers[index] = value;
    setAnswers(newAnswers);
  };

  // 合計スコアを計算
  const calculateTotal = (): number => {
    return answers.reduce((sum, answer) => {
      return sum + (answer >= 0 ? answer : 0);
    }, 0);
  };

  // 最大スコアを計算
  const getMaxScore = (): number => {
    return QUESTIONS.reduce((sum, q) => sum + q.maxScore, 0);
  };

  // 判定を取得（HDSR基準: 30点満点）
  const getJudgment = (score: number): { text: string; className: string } => {
    if (score >= 21) {
      return { text: '正常範囲', className: classes.judgmentNormal };
    } else if (score >= 15) {
      return { text: '軽度認知障害（MCI）の疑い', className: classes.judgmentMCI };
    } else {
      return { text: '認知症の疑い', className: classes.judgmentDementia };
    }
  };

  // 全ての回答が選択されているかチェック
  const canSubmit = (): boolean => {
    if (executing) return false;
    if (!testDate) return false;
    return answers.every(answer => answer >= 0);
  };

  // 日付文字列をタイムスタンプ（ミリ秒）に変換
  const dateToTimestamp = (dateStr: string): number => {
    // 日付の正午（12:00:00）をタイムスタンプとして使用
    const date = new Date(dateStr + 'T12:00:00');
    return date.getTime();
  };

  const handleSubmit = () => {
    const timestamp = dateToTimestamp(testDate);
    const data = {
      answers: answers,
      timestamp: timestamp,
    };

    if (isEdit && currentRecord) {
      onUpdate(uniqueId, String(currentRecord.timestamp), data);
    } else {
      onCreate(uniqueId, data);
    }
  };

  const totalScore = calculateTotal();
  const judgment = getJudgment(totalScore);

  return (
    <div className={classes.root}>
      <Typography variant="body2" className={classes.breadcrumb}>
        {institutionName} &gt; {userName} &gt; MCIテスト &gt; {isEdit ? '編集' : '新規作成'}
      </Typography>

      <div className={classes.header}>
        <IconButton className={classes.backButton} onClick={handleBack}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h5">{isEdit ? 'MCIテスト編集' : 'MCIテスト作成'}</Typography>
      </div>

      <Paper className={classes.form}>
        <Typography variant="body2" className={classes.description}>
          テスト実施日と各質問の回答を選択してください。
        </Typography>

        <TextField
          label="テスト実施日"
          type="date"
          value={testDate}
          onChange={e => setTestDate(e.target.value)}
          className={classes.dateField}
          InputLabelProps={{
            shrink: true,
          }}
          required
          disabled={isEdit}
          helperText={isEdit ? 'テスト日は変更できません' : ''}
        />

        {QUESTIONS.map((question, index) => (
          <div key={question.id} className={classes.questionContainer}>
            <Typography className={classes.questionNumber}>{question.label}</Typography>
            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend" className={classes.questionText}>
                {question.question}
              </FormLabel>
              <Typography className={classes.questionHint}>{question.hint}</Typography>
              <RadioGroup
                value={answers[index] >= 0 ? String(answers[index]) : ''}
                onChange={e => handleAnswerChange(index, parseInt(e.target.value, 10))}
                className={classes.radioGroup}
              >
                <Grid container spacing={1}>
                  {question.options.map(option => (
                    <Grid item xs={12} sm={6} md={4} key={option.value}>
                      <FormControlLabel
                        value={String(option.value)}
                        control={<Radio color="primary" />}
                        label={option.label}
                      />
                    </Grid>
                  ))}
                </Grid>
              </RadioGroup>
            </FormControl>
          </div>
        ))}

        <Divider />

        <div className={classes.totalSection}>
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography className={classes.totalLabel}>合計スコア</Typography>
              <Typography className={classes.totalScore}>
                {totalScore} / {getMaxScore()} 点
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography className={classes.totalLabel}>判定</Typography>
              <Typography className={judgment.className}>{judgment.text}</Typography>
              <Typography variant="caption" color="textSecondary">
                ※簡易スクリーニング結果です
              </Typography>
            </Grid>
          </Grid>
        </div>

        <div className={classes.buttonContainer}>
          <Button variant="outlined" onClick={handleBack} disabled={executing}>
            キャンセル
          </Button>
          <Button variant="contained" color="primary" onClick={handleSubmit} disabled={!canSubmit()}>
            {executing ? <CircularProgress size={24} /> : isEdit ? '更新' : '作成'}
          </Button>
        </div>
      </Paper>
    </div>
  );
};

export default MCIRecordForm;
